/**
 * 行ごと contenteditable のアウトライン編集器。
 *
 * ## なぜ行ごとなのか
 * 文書全体を 1 つの contenteditable にすると、ブラウザが勝手に `<div>` や `<br>` を作り、
 * 階層と DOM の形が食い違っていく。行ごとに区切れば、**編集できる領域は常にプレーンな 1 行**で、
 * 構造の変更は全部こちらの操作として明示的に起きる。Mac 版と同じ意味論を保てるのはこの形だけ。
 *
 * ## 構造の意味論はここに書かない
 * Enter / Backspace / Tab / Shift+Tab が何をするかは `@biside/domain` の
 * `splitPlan` / `mergePlan` / `indentPlan` / `outdentPlan` にある。ゴールデンで Swift と
 * 突き合わせてあるのはそちらで、ここは**キーを計画に翻訳し、結果のキャレットを置くだけ**にする。
 * 画面側に意味論が漏れると、その瞬間から Mac との一致を機械で守れなくなる。
 *
 * ## 描き直しの原則
 * 全部作り直さない。行要素は id で使い回し、変わったところだけ触る。とくに
 * **編集中の行の文字列には外から書き込まない**（IME の未確定文字とキャレットが消えるため）。
 */

import {
  between,
  indentPlan,
  mergePlan,
  outdentPlan,
  splitPlan,
  tags,
  type EditPlan,
  type SyncOp,
} from "@biside/domain";
import { caretIsCollapsed, caretOffset, setCaret, setRowText } from "./caret.ts";
import { visibleRows, type OutlineStore } from "./store.ts";

const newId = () => crypto.randomUUID();

/** 打鍵のたびに送らず、少し止まってからまとめて送る。 */
const TEXT_DEBOUNCE_MS = 250;

export interface OutlineHost {
  store: OutlineStore;
  /** 操作を送る（手元への適用は store が済ませている）。 */
  send(ops: SyncOp[]): void;
  /** ズームが変わったことを外へ伝える（パンくず用）。 */
  onZoom?(nodeId: string | null): void;
  /** 自分がどの行に居るかを外へ伝える。 */
  onMove?(nodeId: string | null): void;
  /** 行 id → そこに居る人。他人の居場所を出すために読む。 */
  presence?(): Map<string, string>;
}

export class OutlineView {
  private rows = new Map<string, HTMLElement>();
  private composing = false;
  private pendingText = new Map<string, string>();
  /** 注記の書きかけ。本文と別に持つ——同じ行の本文と注記を続けて直せるようにするため。 */
  private pendingNote = new Map<string, string>();
  /**
   * 「注記の欄を開いているが、まだ何も書いていない」行。
   *
   * これは**画面の状態であって、同期する内容ではない**。空の注記はドメイン側で「注記なし」に
   * 正規化される（Mac 版の書き込み口と同じ扱い）ので、開いたことを操作として送ると、送った先で
   * 消えて戻ってきて欄が閉じる。開いている事実はこちらで覚える。
   */
  private openNotes = new Set<string>();
  /** 描き直しの最中かどうか。再入を弾くために持つ。 */
  private rendering = false;
  private dirty = false;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** ズームの根。null なら最上位。 */
  zoomId: string | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly host: OutlineHost,
  ) {
    this.bind();
    this.host.store.onChange(() => this.render());
  }

  // ------------------------------------------------------------------ 描画

  /**
   * 描き直し。**再入させない。**
   *
   * 行を消すとブラウザは同期的に blur / focusout を出す。その先で送信が起きれば手元の木が動き、
   * 木が動けば描き直しが呼ばれる——つまり「消している最中に消し直す」が普通に起こる。実際に
   * これで `remove` が「もう子ではない」と例外を投げ、**操作が送られないまま終わった**。
   * 内側の呼び出しは印だけ付けて戻し、外側が終わってからもう一度回す。
   */
  render(): void {
    if (this.rendering) {
      this.dirty = true;
      return;
    }
    this.rendering = true;
    try {
      this.paint();
    } finally {
      this.rendering = false;
    }
    if (this.dirty) {
      this.dirty = false;
      this.render();
    }
  }

  private paint(): void {
    const rows = visibleRows(this.host.store.tree, this.zoomId);
    const activeId = this.focusedRowId();
    const seen = new Set<string>();
    // 並べ替えで自分の行が動くと、Chrome は一度 DOM から外れた扱いにしてキャレットを落とす。
    // 「上の行に誰かが差し込んだ瞬間に自分の入力位置が飛ぶ」という壊れ方がこれ。控えて戻す。
    const activeText = this.focusedText();
    const keptOffset = activeText ? caretOffset(activeText) : null;

    rows.forEach((row, index) => {
      const el = this.ensureRow(row.node.id);
      seen.add(row.node.id);

      el.style.setProperty("--depth", String(row.depth));
      el.classList.toggle("has-children", row.hasChildren);
      el.classList.toggle("collapsed", !!row.node.collapsed);
      el.classList.toggle("done", !!row.node.completed);

      // 他人の居場所。名前をそのまま出すと長いので頭文字だけ添える。
      const who = this.host.presence?.().get(row.node.id) ?? "";
      if (el.dataset.who !== who) {
        el.dataset.who = who;
        el.classList.toggle("occupied", who !== "");
        el.querySelector<HTMLElement>(".who")!.textContent = who ? who.slice(0, 2) : "";
        el.querySelector<HTMLElement>(".who")!.title = who;
      }

      const text = el.querySelector<HTMLElement>(".text")!;
      // 編集中の行には外から書き込まない。IME の未確定文字ごと消えるため。
      const editing = row.node.id === activeId && (this.composing || this.pendingText.has(row.node.id));
      if (!editing) setRowText(text, row.node.text);
      this.paintTags(el, row.node.text);

      const note = el.querySelector<HTMLElement>(".note")!;
      const hasNote = row.node.note != null || this.openNotes.has(row.node.id);
      note.hidden = !hasNote;
      // 注記も本文と同じ扱い。書きかけ・変換中は外から触らない。
      const editingNote = note === document.activeElement && (this.composing || this.pendingNote.has(row.node.id));
      if (hasNote && !editingNote) setRowText(note, row.node.note ?? "");

      // 並べ替えは差分で。要る場所に無いときだけ動かす。
      if (this.container.children[index] !== el) {
        this.container.insertBefore(el, this.container.children[index] ?? null);
      }
    });

    for (const [id, el] of this.rows) {
      if (seen.has(id)) continue;
      el.remove();
      this.rows.delete(id);
    }

    if (activeId && keptOffset != null && this.focusedRowId() !== activeId) {
      this.refocus(activeId, keptOffset);
    }

    this.container.classList.toggle("empty", rows.length === 0);
  }

  private ensureRow(id: string): HTMLElement {
    const existing = this.rows.get(id);
    if (existing) return existing;

    const el = document.createElement("div");
    el.className = "row";
    el.dataset.id = id;
    el.innerHTML =
      `<button class="twist" tabindex="-1" aria-label="折りたたむ"></button>` +
      `<button class="bullet" tabindex="-1" aria-label="ズーム"></button>` +
      `<div class="text" contenteditable="plaintext-only" spellcheck="false"></div>` +
      `<div class="chips"></div>` +
      `<span class="who"></span>` +
      `<div class="note" contenteditable="plaintext-only" spellcheck="false" hidden></div>`;
    this.rows.set(id, el);
    return el;
  }

  /** タグは行の外に丸窓で出す。本文そのものは素のテキストのまま保つ。 */
  private paintTags(el: HTMLElement, text: string): void {
    const found = tags(text);
    const box = el.querySelector<HTMLElement>(".chips")!;
    const want = found.map((t) => t.key).join(" ");
    if (box.dataset.want === want) return;
    box.dataset.want = want;
    box.textContent = "";
    for (const t of found) {
      const chip = document.createElement("span");
      chip.className = "chip";
      chip.textContent = t.key;
      box.appendChild(chip);
    }
  }

  // ------------------------------------------------------------------ 入力

  private bind(): void {
    this.container.addEventListener("keydown", (e) => this.onKeyDown(e));
    this.container.addEventListener("focusin", () => this.host.onMove?.(this.focusedRowId()));
    this.container.addEventListener("input", () => this.onInput());
    this.container.addEventListener("compositionstart", () => {
      this.composing = true;
    });
    this.container.addEventListener("compositionend", () => {
      this.composing = false;
      this.onInput();
    });
    this.container.addEventListener("focusout", (e) => {
      // 開いただけで何も書かずに離れた注記は畳む。書いていないので送るものも無い。
      const from = e.target as HTMLElement;
      if (from?.classList.contains("note") && (from.textContent ?? "").trim() === "") {
        const id = from.closest<HTMLElement>(".row")?.dataset.id;
        if (id) this.openNotes.delete(id);
      }
      this.flushText();
      // 描き直しは今の処理が終わってから。focusout はしばしば描き直しの内側で起きるので、
      // ここで直に呼ぶと再入する。
      queueMicrotask(() => this.render());
    });
    this.container.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const id = target.closest<HTMLElement>(".row")?.dataset.id;
      if (!id) return;
      if (target.classList.contains("twist")) this.toggleCollapsed(id);
      else if (target.classList.contains("bullet")) this.zoomTo(id);
    });
    window.addEventListener("beforeunload", () => this.flushText());
    // タブを裏に回すと、ブラウザはタイマーを間引く（この検証中に実測した）。まとめ送りを待って
    // いる最中に切り替えると、打った文字が送られないまま宙に浮く。隠れる瞬間に必ず出し切る。
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") this.flushText();
    });
  }

  private focusedRowId(): string | null {
    const el = document.activeElement as HTMLElement | null;
    return el?.closest<HTMLElement>(".row")?.dataset.id ?? null;
  }
  private focusedText(): HTMLElement | null {
    const el = document.activeElement as HTMLElement | null;
    return el?.classList.contains("text") ? el : null;
  }

  /** いま見えている文字列を控える。送るのは少し後（`flushText`）。 */
  private onInput(): void {
    if (this.composing) return; // 未確定のうちは送らない
    const id = this.focusedRowId();
    const el = document.activeElement as HTMLElement | null;
    if (!id || !el) return;
    if (el.classList.contains("note")) this.pendingNote.set(id, el.textContent ?? "");
    else if (el.classList.contains("text")) this.pendingText.set(id, el.textContent ?? "");
    else return;
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => this.flushText(), TEXT_DEBOUNCE_MS);
  }

  /**
   * 控えていた文字列を操作にして送る。
   *
   * 構造を変える前には**必ずこれを通す**。分割も結合も木の中の文字列を読んで計画を立てるので、
   * 打ったばかりの文字が store に入っていないと、消えた状態で切られてしまう。
   */
  flushText(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.pendingText.size === 0 && this.pendingNote.size === 0) return;
    const ops: SyncOp[] = [];
    for (const [nodeId, text] of this.pendingText) {
      if (this.host.store.tree.get(nodeId)?.text === text) continue;
      ops.push({ opId: newId(), kind: "update", nodeId, text });
    }
    for (const [nodeId, raw] of this.pendingNote) {
      // 空白だけの注記は「注記なし」に寄せる。Mac 側の書き込み口と同じ扱い。
      const note = raw.trim() === "" ? null : raw;
      if (note == null) this.openNotes.delete(nodeId); // 空にしたら欄ごと畳む
      if (this.host.store.tree.get(nodeId)?.note === note) continue;
      ops.push({ opId: newId(), kind: "updateNote", nodeId, note });
    }
    this.pendingText.clear();
    this.pendingNote.clear();
    if (ops.length) this.host.send(ops);
  }

  private onKeyDown(e: KeyboardEvent): void {
    // 変換中のキーは IME のもの。触ると確定操作が壊れる。
    if (e.isComposing || e.keyCode === 229) return;

    const id = this.focusedRowId();
    const text = document.activeElement as HTMLElement | null;
    if (!id || !text?.isContentEditable) return;
    // 注記を書いているときに構造を動かすと、書いているつもりで行が割れる。Enter だけ受ける。
    const inNote = text.classList.contains("note");
    if (inNote && e.key !== "Enter" && e.key !== "Escape") {
      // Tab は**止めないと**ブラウザが焦点を次の要素へ運んでしまう。何もしないつもりで
      // 素通しすると、注記から抜けた状態で次のキーが本文に効く。
      if (e.key === "Tab") e.preventDefault();
      return;
    }

    switch (e.key) {
      case "Enter": {
        e.preventDefault();
        if (e.metaKey || e.ctrlKey) return this.toggleCompleted(id);
        if (e.shiftKey) return this.editNote(id);
        if (inNote) return this.refocus(id, Infinity); // 注記から本文へ戻る
        const offset = caretOffset(text) ?? 0;
        this.flushText();
        this.run(splitPlan(this.host.store.tree, id, offset, newId));
        return;
      }
      case "Tab": {
        e.preventDefault();
        const keep = caretOffset(text) ?? 0; // 描き直しの前に控える
        this.flushText();
        this.run(e.shiftKey ? outdentPlan(this.host.store.tree, id, newId) : indentPlan(this.host.store.tree, id, newId));
        this.refocus(id, keep); // 字下げ・字上げはキャレットを動かさない
        return;
      }
      case "Backspace": {
        if (caretOffset(text) !== 0 || !caretIsCollapsed()) return; // 通常の 1 文字削除に任せる
        e.preventDefault();
        this.flushText();
        this.run(mergePlan(this.host.store.tree, id, newId));
        return;
      }
      case "ArrowUp":
      case "ArrowDown": {
        if (e.altKey || e.metaKey) return;
        const moved = this.moveFocus(id, e.key === "ArrowUp" ? -1 : 1, caretOffset(text) ?? 0);
        if (moved) e.preventDefault();
        return;
      }
      case "Escape": {
        if (this.zoomId) {
          e.preventDefault();
          this.zoomTo(this.host.store.tree.get(this.zoomId)?.parentId ?? null);
        }
        return;
      }
    }
  }

  /** 計画を実行し、指示があればそこへキャレットを置く。 */
  private run(plan: EditPlan): void {
    if (plan.ops.length === 0) return;
    this.host.send(plan.ops);
    if (plan.focus) this.refocus(plan.focus.nodeId, plan.focus.offset);
  }

  private refocus(nodeId: string, offset: number): void {
    const text = this.rows.get(nodeId)?.querySelector<HTMLElement>(".text");
    if (!text) return;
    text.focus();
    setCaret(text, offset);
  }

  /** 上下の行へ。端に来たらブラウザの既定に戻す（false を返す）。 */
  private moveFocus(fromId: string, delta: number, offset: number): boolean {
    const rows = visibleRows(this.host.store.tree, this.zoomId);
    const at = rows.findIndex((r) => r.node.id === fromId);
    const next = rows[at + delta];
    if (at < 0 || !next) return false;
    this.refocus(next.node.id, Math.min(offset, next.node.text.length));
    return true;
  }

  // ------------------------------------------------------------------ 折りたたみ・ズーム

  /** 完了の印。会話や履歴は消さない（Mac 版と同じ扱い）。 */
  toggleCompleted(nodeId: string): void {
    const node = this.host.store.tree.get(nodeId);
    if (!node) return;
    this.host.send([{ opId: newId(), kind: "setCompleted", nodeId, value: !node.completed }]);
  }

  /** 注記を開いて書き始める。まだ無ければ空で作る。 */
  editNote(nodeId: string): void {
    const node = this.host.store.tree.get(nodeId);
    if (!node) return;
    this.openNotes.add(nodeId);
    this.render();
    const note = this.rows.get(nodeId)?.querySelector<HTMLElement>(".note");
    if (!note) return;
    note.hidden = false;
    note.focus();
    setCaret(note, (node.note ?? "").length);
  }

  toggleCollapsed(nodeId: string): void {
    const node = this.host.store.tree.get(nodeId);
    if (!node) return;
    this.host.send([{ opId: newId(), kind: "setCollapsed", nodeId, value: !node.collapsed }]);
  }

  zoomTo(nodeId: string | null): void {
    this.flushText();
    this.zoomId = nodeId;
    this.render();
    this.host.onZoom?.(nodeId);
  }

  /** 最下段に行を足す（何も無いときの入口）。 */
  appendRow(): void {
    const siblings = this.host.store.tree.children(this.zoomId);
    const last = siblings[siblings.length - 1];
    const plan: SyncOp[] = [
      {
        opId: newId(),
        kind: "insert",
        nodeId: newId(),
        parentId: this.zoomId,
        position: between(last?.position ?? null, null),
        text: "",
      },
    ];
    this.host.send(plan);
    const inserted = plan[0]!;
    if (inserted.kind === "insert") this.refocus(inserted.nodeId, 0);
  }
}
